/**
 * @copyright © 2026 ASAP Technology. All rights reserved.
 * @license BSL-1.1 (see LICENSE file)
 *
 * @file App.tsx
 * @description Main application component for Payment Form
 */
import { useState, useEffect } from 'react';
import PaymentForm from '@/components/PaymentForm';
import type { StylesPayload } from '@/components/PaymentForm';
import { LocaleProvider } from '@/utils/LocaleContext';

interface FormConfig {
  formToken: string;
  fields: Record<string, { name: string; id: string }>;
  honeypots: Array<{ name: string; id: string; type: string; position: number }>;
}

export default function App() {
  const [styles, setStyles] = useState<StylesPayload | undefined>();
  const [formConfig, setFormConfig] = useState<FormConfig | null>(null);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [allowedEmbedderOrigins, setAllowedEmbedderOrigins] = useState<string[]>([]);
  const [integrationMode, setIntegrationMode] = useState<string>('redirect');
  const [locale, setLocale] = useState<string | undefined>();
  const [allowAmountEdit, setAllowAmountEdit] = useState<boolean>(false);
  const [showInstallments, setShowInstallments] = useState<boolean>(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    // 1. Fetch config with honeypots and obfuscated field mapping
    fetch('/api/form-config')
      .then(res => res.json())
      .then((config: FormConfig) => {
        setFormConfig(config);

        if (!token) {
          // Allow fallback to styles.json ONLY if not running with a token, for local UI testing
          // In production, token will be mandatory.
      fetch('/styles.json')
        .then((res) => res.json())
        .then((data: StylesPayload) => {
          if (data.Styles && data.Styles.length > 0) {
            setStyles(data);
          }
          setIsLoading(false);
        })
        .catch(() => { setIsLoading(false); });
      return;
        }

        // 2. Fetch order context
        return fetch('/api/order/get', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, formToken: config.formToken })
        });
      })
      .then(async (res) => {
        if (!res) return; // Fallback took over
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.responseMessage || 'Error al recuperar orden');
        }
        return res.json();
      })
      .then((data) => {
        if (!data) return;

        // El navegador consume únicamente la proyección pública creada por el
        // backend. El Configuration libre del Core nunca cruza este límite.
        const display = data.displayConfig || {};
        const publicConfiguration = data.Configuration || {};
        const additional = publicConfiguration.AdditionalConfig || {};

        const styles = (display.styles && display.styles.length > 0) ? display.styles
                     : [];

        // Extract Form styles overrides
        const formStyle = styles.find((s: any) => s && typeof s === 'object' && 'Form' in s)?.Form || {};

        const formMode = formStyle.form_mode || formStyle.formMode || display.formMode || "classic";
        const fingerprintLevel = display.fingerprintLevel ?? 1;
        const showProducts = formStyle.show_products ?? formStyle.showProducts ?? display.showProducts ?? true;
        const productLayout = formStyle.product_layout || formStyle.productLayout || display.productLayout;
        const merchantLogoUrl = display.merchantLogoUrl;
        const merchantName = display.merchantName;
        const resolvedLocale = formStyle.locale || formStyle.Locale || display.locale;

        const showInstallments = formStyle.allow_installments ?? formStyle.allowInstallments ?? formStyle.show_installments ?? formStyle.showInstallments ?? display.showInstallments ?? true;
        const allowAmountEdit = formStyle.allow_amount_edit ?? formStyle.allowAmountEdit ?? display.allowAmountEdit ?? false;

        setLocale(resolvedLocale);
        setShowInstallments(showInstallments);
        setAllowAmountEdit(allowAmountEdit);
        setAllowedEmbedderOrigins(display.allowedEmbedderOrigins || []);
        setIntegrationMode(display.integrationMode || 'redirect');

        setStyles({
          amount: data.transactionAmount || data.TransactionAmount,
          currency_code: data.currencyCode || data.CurrencyCode,
          reference_number: data.reference || data.ReferenceNumber,
          initial_token: data.initialToken,
          merchant_redirect_url: data.merchantRedirectURL,
          redirect_delay_ms: display.redirectDelayMs ?? additional.RedirectDelayMs,
          merchant_logo_url: merchantLogoUrl,
          merchant_name: merchantName,
          product_layout: productLayout,
          fingerprint_level: fingerprintLevel,
          form_mode: formMode,
          products: showProducts ? (data.Products || data.products) : undefined,
          enable_qr: display.enableQr ?? additional.EnableQRDisplay ?? false,
          qr_base64: data.qrBase64,
          transaction_valid_thru: data.transactionValidThru || data.TransactionValidThru,
          available_payment_methods: data.paymentMethods,
          preferred_payment_method_id: data.preferredPaymentMethodId,
          status_tracking_enabled: data.statusTrackingEnabled ?? false,
          configuration: publicConfiguration,
          Styles: styles
        });
        setIsLoading(false);
      })
      .catch((err) => {
        setOrderError(err.message);
        setIsLoading(false);
      });
  }, []);

  // Apply background color to body
  useEffect(() => {
    if (!styles?.Styles) return;
    const bgEntry = styles.Styles.find((e) => 'Background' in e);
    if (bgEntry && 'Background' in bgEntry && bgEntry.Background.color) {
      document.body.style.backgroundColor = bgEntry.Background.color;
      document.body.style.backgroundImage = 'none';
    }
    return () => {
      document.body.style.backgroundColor = '';
      document.body.style.backgroundImage = '';
    };
  }, [styles]);

  if (isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: 'white' }}>Cargando orden...</div>;
  }

  if (orderError) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: 'white' }}>Error: {orderError}</div>;
  }

  return (
    <LocaleProvider defaultLocale={locale}>
      <main className="main-container">
        <PaymentForm
          styles={styles}
          amount={styles?.amount}
          currencyCode={styles?.currency_code}
          planId={styles?.plan_id}
          referenceNumber={styles?.reference_number}
          initialToken={styles?.initial_token}
          merchantRedirectUrl={styles?.merchant_redirect_url}
          redirectDelay={styles?.redirect_delay_ms}
          merchantLogoUrl={styles?.merchant_logo_url}
          merchantName={styles?.merchant_name}
          productLayout={styles?.product_layout}
          fingerprintLevel={styles?.fingerprint_level}
          formMode={styles?.form_mode}
          products={styles?.products}
          enableQr={styles?.enable_qr}
          qrBase64={styles?.qr_base64}
          transactionValidThru={styles?.transaction_valid_thru}
          paymentMethods={styles?.available_payment_methods}
          preferredPaymentMethodId={styles?.preferred_payment_method_id}
          statusTrackingEnabled={styles?.status_tracking_enabled}
          configuration={styles?.configuration}
          formToken={formConfig!.formToken}
          formConfig={formConfig!}
          allowedEmbedderOrigins={allowedEmbedderOrigins}
          integrationMode={integrationMode}
          allowAmountEdit={allowAmountEdit}
          showInstallments={showInstallments}
        />
      </main>
    </LocaleProvider>
  );
}
